import axios from "axios"
import config from "aldoo-ra/Config"
import { useEffect } from "react"
import Value from "aldoo-ra/Value"
import diagnostics from "./diagnostics"
import { Encrypt, Decrypt } from "aldoo-ra/AldooDecrypt"

const { PLATFORM_API_URL, PLATFORM_API_VERSION, API_DIAGNOSTICS, INTERNAL_API_URL, API_ENDPOINT_OVERRIDES } = config

// SSR runs inside the web-app server process and can reach the REST API
// directly over loopback. Going through the public domain on every render
// adds CDN/TLS latency and exposes SSR to upstream errors (e.g. CF 5xx).
// When INTERNAL_API_URL is set, SSR uses it; the browser bundle keeps the
// public PLATFORM_API_URL.
const API_BASE_URL =
  (typeof window === "undefined" && INTERNAL_API_URL) || PLATFORM_API_URL

//constructs an api end point to the API
//API_ENDPOINT_OVERRIDES maps a channel (the first path segment of the endpoint
//name, e.g. "order" for "order/tus") to an alternate API base URL — used when
//some channels are served by a dedicated backend instance (e.g. file transfers)
export const ep = (name) => {
  const channel = String(name).split("/")[0]
  const base = API_ENDPOINT_OVERRIDES?.[channel] || API_BASE_URL
  return `${base}/${PLATFORM_API_VERSION}/${name}`
}

//If the sanitize function is called on an object
//if will remove the ignored fields
const IGNORED_FIELDS_ON_SANITIZE = ["_id"]

//Send only meaningful parameters to the rest api backend
//@param payload Table Returns a table that has the ignore fields stripped
//@param isRootLevel Boolean Whether this is the root level of sanitization
const sanitize = (payload, isRootLevel = true) => {
  const result = {}
  const keys = Object.keys(payload)
  for (const key of keys) {
    const value = payload[key]
    //undefined values (but allow null values and false booleans)
    if (value === undefined) continue

    // Only ignore _id at root level, not in nested objects like match queries
    const shouldIgnoreField = isRootLevel && ~IGNORED_FIELDS_ON_SANITIZE.indexOf(key)

    //Empty string values (but allow false, null, and 0)
    const shouldSkipValue = typeof value === "string" && value.length === 0

    if (
      key &&
      !shouldSkipValue &&
      !shouldIgnoreField
    ) {
      //if the value is an object, sanitize it (but not null values)
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        !(value instanceof Date) &&
        !(typeof window !== "undefined" && value instanceof File)
      ) {
        // Pass false for isRootLevel to allow _id in nested objects
        result[key] = sanitize(value, false)
      } else {
        result[key] = value
      }
    }
  }
  return result
}

//This is a reference to the Value('user-credentials') object
//It changes based on the user login state
let USER_CREDENTIALS = null
let CACHED_CREDENTIALS = null // Cache the last known good credentials
let frameWait = true

let API_AUTH_KEY = null
//set the API_AUTH_KEY prior to making any api calls
export const setAPIAuthKey = (key) => {
  API_AUTH_KEY = key
}

// Workspace switcher context — the org id every request is scoped to.
// Set by the workspace UI when the user picks an org from the switcher.
// Read on every request and forwarded as `x-active-org` so the API can
// scope org-dependent actions without every call site having to pass it.
let ACTIVE_ORG_ID = null
export const setActiveOrg = (orgId) => {
  ACTIVE_ORG_ID = orgId || null
  if (typeof window !== "undefined") {
    try {
      if (orgId) window.localStorage?.setItem("active-org-id", String(orgId))
      else window.localStorage?.removeItem("active-org-id")
    } catch { /* private mode / SSR / etc. */ }
  }
}
export const getActiveOrg = () => ACTIVE_ORG_ID
// Restore on module load so a page refresh keeps the switcher's pick.
if (typeof window !== "undefined") {
  try { ACTIVE_ORG_ID = window.localStorage?.getItem("active-org-id") || null } catch {}
}

//get current user credentials
export const getCurrentUser = () => {
  return USER_CREDENTIALS
}

// Encryption configuration
let ENCRYPTION_ENABLED = false
let ENCRYPTION_KEY = null

/**
 * Configure API encryption settings
 * @param {Object} options - Encryption options
 * @param {boolean} options.enabled - Enable/disable encryption
 * @param {string} options.key - Base64 encoded encryption key
 */
export const configureEncryption = (options = {}) => {
  ENCRYPTION_ENABLED = options.enabled || false
  ENCRYPTION_KEY = options.key || null
}

/**
 * Check if encryption should be used for this request
 * @param {Object} options - Request options
 * @returns {boolean} True if encryption should be used
 */
const shouldUseEncryption = (options = {}) => {
  // Explicit option overrides
  if (options.encrypt === false) return false
  if (options.encrypt === true) return ENCRYPTION_ENABLED && !!ENCRYPTION_KEY

  // Default: encrypt if globally enabled and key available
  return ENCRYPTION_ENABLED && !!ENCRYPTION_KEY
}

/**
 * This is added to the application start up and listens for
 * available user credentials. Those are then used to make
 * api calls to the backend
 */
export const AldooAPI = () => {
  const [auth] = Value("user-credentials")

  useEffect(() => {
    //here we get the value from the auth object
    //if it's not available, we try go get the value immediately


    USER_CREDENTIALS = auth

    // Cache the credentials if they're valid (have auth_token)
    if (auth && auth.auth_token) {
      CACHED_CREDENTIALS = auth
    }
  }, [auth])
}

/**
 * All API calls are made using the post method
 * @param {*} url
 * @param {*} payload
 * @param {*} options
 * @returns
 */
const post = async (url, payload, options) => {
  //Here we wait for a single frame to pass
  //so that React can update it's effects
  if (frameWait) {
    await new Promise((resolve) => setTimeout(resolve, 0))
    frameWait = false
  }

  // Additional wait for React effects to complete if credentials are not available
  // This addresses the race condition where useEffect runs during API execution
  if (!USER_CREDENTIALS) {
    await new Promise((resolve) => setTimeout(resolve, 10)) // Wait 10ms for React effects
  }

  //options = { headers: { Authorization: `Bearer ${token}` } }
  if (!options) options = {}
  //check for headers and add the USER_CREDENTIALS
  if (!options.headers) options.headers = {}

  // Check if custom auth was provided via options
  let credentials = options.customAuth

  // If no custom auth, try to get credentials from USER_CREDENTIALS (React managed)
  if (!credentials) {
    credentials = USER_CREDENTIALS
  }

  // If not available, try to get directly from Value store (fallback for timing issues)
  // Only attempt this on client-side
  if (!credentials && typeof window !== 'undefined') {
    try {
      // Import and use the synchronous getValue function from Value store
      const { getValue } = await import("aldoo-ra/Value")
      if (typeof getValue === 'function') {
        credentials = getValue("user-credentials", "user-credentials")
      }
    } catch (error) {
      // Value store not available or other error, continue without credentials
      console.warn("Could not access user credentials for API call", error)
    }
  }

  // Final fallback: use cached credentials (but not if custom auth was attempted)
  if (!credentials && !options.customAuth && CACHED_CREDENTIALS) {
    credentials = CACHED_CREDENTIALS
  }

  //check if we have user credentials, if so add them to the headers
  if (credentials) {
    const { auth_token } = credentials
    if (auth_token) {
      options.headers["Authorization"] = auth_token
    }
  }


  if (API_AUTH_KEY) {
    options.headers["aldoo-api-auth-key"] = API_AUTH_KEY
  }

  if (ACTIVE_ORG_ID && !options.headers["x-active-org"]) {
    options.headers["x-active-org"] = ACTIVE_ORG_ID
  }

  // Handle encryption if enabled
  let finalPayload = payload
  const useEncryption = shouldUseEncryption(options)

  // Check if payload is FormData (for file uploads)
  const isFormData = typeof window !== "undefined" && payload instanceof FormData

  if (useEncryption && !isFormData) {
    try {
      // Add encryption header
      options.headers["aldoo-encrypt"] = "true"

      // Encrypt the payload
      const jsonPayload = JSON.stringify(sanitize(payload))
      const encryptedData = await Encrypt(jsonPayload, ENCRYPTION_KEY)

      // Wrap in ald-x envelope
      finalPayload = { 'ald-x': encryptedData }

    } catch (error) {
      console.error('❌ Encryption failed:', error)
      throw new Error(`Request encryption failed: ${error.message}`)
    }
  } else if (isFormData) {
    // FormData payloads cannot be encrypted, pass through as-is
    finalPayload = payload
  } else {
    // Sanitize non-encrypted payloads
    finalPayload = sanitize(payload)
  }

  const response = await axios.post(url, finalPayload, options)
  let responseData = response.data

  // Handle encrypted responses - decrypt if backend sent encrypted response and we have a key
  if (response.headers['aldoo-encrypt'] === 'true' && responseData['ald-x'] && ENCRYPTION_KEY) {
    try {
      const decryptedJson = await Decrypt(responseData['ald-x'], ENCRYPTION_KEY)
      responseData = JSON.parse(decryptedJson)
    } catch (error) {
      console.error('❌ Response decryption failed:', error)
      // Fall back to original response data if decryption fails
    }
  }

  //if enabled, print the diagnostics for the requests
  if (API_DIAGNOSTICS)
    diagnostics({
      response: responseData,
      url,
      payload: finalPayload,
      options,
      encrypted: useEncryption
    })

  // Emit API response events for external systems (like SessionMonitor)
  try {
    // Capture the current page URL at the time of API call
    const currentPageUrl = typeof window !== "undefined" ? window.location.href : null

    // Emit general API_RESPONSE_DATA event for all responses
    const responseDataEvent = new CustomEvent("API_RESPONSE_DATA", {
      detail: {
        responseData,
        url,
        payload: finalPayload,
        originalPayload: payload, // Include original unsanitized payload
        options,
        encrypted: useEncryption,
        currentPageUrl // Add current page URL
      }
    })
    if (typeof window !== 'undefined') {
      window.dispatchEvent(responseDataEvent)
    }

    // Emit specific API_RESPONSE_ERROR event if response contains an error
    if (responseData?.error) {
      const errorEvent = new CustomEvent("API_RESPONSE_ERROR", {
        detail: {
          responseData,
          url,
          payload: finalPayload,
          originalPayload: payload, // Include original unsanitized payload
          options,
          encrypted: useEncryption,
          error: responseData.error,
          currentPageUrl // Add current page URL
        }
      })
      if (typeof window !== 'undefined') {
        window.dispatchEvent(errorEvent)
      }
    }
  } catch (eventError) {
    // Silently handle event emission errors to not break API flow
    console.warn("API: Failed to emit response events:", eventError)
  }

  return responseData
}

/**
 * This constructs an api call to a specific end point
 * Then the actions in the local module api reuse that for code clarity
 * @param {*} endPoint - The API endpoint to call
 * @param {*} customAuth - Optional custom auth object { auth_token, email, deviceID, _id } to use instead of PlatformAuth
 * @returns
 */
export const api = (endPoint, customAuth) => {
  return async (payload, options) => {
    // If custom auth is provided, merge it into options
    if (customAuth && customAuth.auth_token) {
      options = {
        ...options,
        customAuth: customAuth
      }
    } else if (customAuth) {
      console.warn('⚠️ [API] Custom auth object missing auth_token:', customAuth)
    }
    return await post(ep(endPoint), payload, options)
  }
}

/**
 * Generates a complete CRUD (Create, Read, Update, Delete) interface for a given model
 *
 * @param {Function} api - The API function to use for making requests (typically from the api() factory)
 * @param {string} model - The model name/identifier to perform CRUD operations on
 *
 * @returns {Object} An object containing CRUD methods:
 * @returns {Function} returns.Get - Retrieves records matching the given criteria
 * @returns {Function} returns.Create - Creates a new record
 * @returns {Function} returns.Update - Updates an existing record
 * @returns {Function} returns.SoftDelete - Marks a record as deleted without removing it
 * @returns {Function} returns.HardDelete - Permanently removes a record
 * @returns {Function} returns.Save - Smart save that creates or updates based on presence of _id
 *
 * @example
 * // Create a CRUD interface for users
 * const userAPI = api('users')
 * const userCRUD = generateCRUDInterface(userAPI, 'User')
 *
 * // Get all users
 * const users = await userCRUD.Get({ match: { active: true } })
 *
 * // Create a new user
 * const newUser = await userCRUD.Create({ name: 'John', email: 'john@example.com' })
 *
 * // Update existing user
 * const updatedUser = await userCRUD.Update({ _id: '123', name: 'Jane' })
 *
 * // Smart save (create if no _id, update if _id exists)
 * const savedUser = await userCRUD.Save({ name: 'Bob' }) // Creates new
 * const updatedUser = await userCRUD.Save({ _id: '123', name: 'Bob Updated' }) // Updates existing
 *
 * // Soft delete (marks as deleted but keeps in database)
 * await userCRUD.SoftDelete({ _id: '123' })
 *
 * // Hard delete (permanently removes from database)
 * await userCRUD.HardDelete({ _id: '123' })
 */
export const generateCRUDInterface = (api, model) => {
  const Get = async (get) => {
    get = {
      ...get,
      model,
      //filter out deleted items
      ...{ match: { ...get.match, deleted: { $ne: true } } },
    }
    return await api({ get })
  }

  const Create = async (data) => {
    const create = { model, data }
    return await api({ create })
  }

  const Update = async (data) => {
    const update = { model, data }
    return await api({ update })
  }

  const SoftDelete = async (data) => {
    const payload = {
      model,
      mode: "soft",
      match: { _id: data._id },
    }
    return await api({ delete: payload })
  }

  const HardDelete = async (data) => {
    const payload = {
      model,
      mode: "hard",
      match: { _id: data._id },
    }
    return await api({ delete: payload })
  }

  const Save = async (data) => {
    if (!data) return
    if (data._id) return await Update(data)
    if (!data._id) return await Create(data)
  }

  return {
    Get,
    Create,
    Update,
    SoftDelete,
    HardDelete,
    Save,
  }
}
